import { useCallback, useEffect, useState } from 'react'

import { useMsal } from '@azure/msal-react'

import { CONTROL_CENTRE_V2_API_DOMAIN } from '@/config/constants'
import useAuthenticatedQuery from '@/hooks/useAuthenticatedQuery'
import type { FeatureFlagsResponse, FeatureFlagsT } from '@/types'

type GetFeatureFlag = (featureFlagKey: string) => boolean

type UseFeatureFlagsReturn = {
  loading: boolean
  error: Error | null
  getFeatureFlag: GetFeatureFlag
  featureFlags: FeatureFlagsT
}

const useFeatureFlags = (): UseFeatureFlagsReturn => {
  const { accounts } = useMsal()
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsT>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const featureFlagsQuery = useAuthenticatedQuery<FeatureFlagsResponse>(
    ['feature-flags'],
    `${CONTROL_CENTRE_V2_API_DOMAIN}/user_feature_flags`,
    {
      enabled: accounts.length > 0,
      refetchInterval: Infinity,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  )

  useEffect(() => {
    if (featureFlagsQuery?.status === 'success' && featureFlagsQuery?.data) {
      const flags = featureFlagsQuery?.data?.data || {}
      setFeatureFlags(flags)
      setLoading(false)
      setError(null)
    } else if (featureFlagsQuery?.status === 'error') {
      setError(featureFlagsQuery.error as Error)
      setLoading(false)
    }
  }, [featureFlagsQuery])

  const getFeatureFlag = useCallback<GetFeatureFlag>(
    (featureFlagKey: string) => {
      if (!featureFlags) return false
      return featureFlags[featureFlagKey] || false
    },
    [featureFlags],
  )

  return {
    loading,
    error,
    getFeatureFlag,
    featureFlags,
  }
}

export default useFeatureFlags
